import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, File, WarningCircle, Paperclip, Trash,
} from '@phosphor-icons/react';
import { notifyError, notifySuccess } from '../../../lib/notify';
import {
  API,
  authHeaders,
  ticketProductLabel,
  fetchTicketComments,
  isTicketEditable,
  canUploadTicketAttachment,
  formatApiError,
  ticketAssigneeLabel,
  ticketCategoryLabel,
  ticketClientBranchLabel,
} from '../api/ticketsApi';
import { useAuth } from '../../../App';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketDomainBadge from '../components/TicketDomainBadge';
import TicketTimeline from '../components/TicketTimeline';
import TicketWorkTimePanel from '../components/TicketWorkTimePanel';
import TicketClientChat from '../components/TicketClientChat';
import DynamicTicketForm from '../components/DynamicTicketForm';
import { Surface, SectionTitle, SkeletonBlock, Alert } from '../ui';
import { useTicketPermissions } from '../hooks/useTicketPermissions';
import useVisibleInterval from '../../../hooks/useVisibleInterval';
import {
  backLabelForPath,
  navigateTicketAction,
  resolveListFrom,
} from '../ticketNav';

function Field({ label, value }) {
  return (
    <div>
      <label className="tk-label">{label}</label>
      <div className="tk-field-readonly">{value || '—'}</div>
    </div>
  );
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

const DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'work', label: 'Work time' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'chat', label: 'Client chat' },
  { id: 'activity', label: 'Activity' },
];

function AttachmentView({ ticketNo, attachment }) {
  const { filename, size, uploaded_by_name } = attachment;
  const contentType = attachment.content_type || attachment.mime_type || '';
  const isImage = contentType.startsWith('image/') || IMAGE_EXT.test(filename || '');
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`${API}/${ticketNo}/attachments/${encodeURIComponent(filename)}`, {
      withCredentials: true,
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [ticketNo, filename]);

  return (
    <div className="tk-attach-card">
      {isImage && url ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img
            src={url}
            alt={filename}
            style={{ width: '100%', height: '110px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
          />
        </a>
      ) : (
        <div className="tk-attach-placeholder">
          {err ? <WarningCircle size={28} weight="duotone" color="#B91C1C" /> : <File size={28} weight="duotone" />}
        </div>
      )}
      <div style={{ fontSize: '12px', marginTop: '6px', wordBreak: 'break-word', fontWeight: 500 }}>{filename}</div>
      {size ? <div className="tk-mono" style={{ fontSize: '11px', color: '#94A3B8' }}>{Math.round(size / 1024)} KB</div> : null}
      {uploaded_by_name ? <div style={{ fontSize: '11px', color: '#94A3B8' }}>by {uploaded_by_name}</div> : null}
      {url && (
        <a href={url} download={filename} className="tk-link" style={{ fontSize: '12px', display: 'inline-block', marginTop: '4px' }}>
          {isImage ? 'Open / Download' : 'Download'}
        </a>
      )}
      {err && <div style={{ fontSize: '12px', color: '#B91C1C' }}>Failed to load</div>}
    </div>
  );
}

function TicketDetailPage() {
  const { ticketNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role === 'end_user' ? 'client' : user?.role;
  const isManagerish = ['super_admin', 'admin', 'manager', 'lead'].includes(role);
  const isStaff = isManagerish || role === 'member' || role === 'agent';
  const isPlatformAdmin = ['super_admin', 'admin'].includes(role);
  const { canAccess } = useTicketPermissions();
  const canTransfer = canAccess('transfer');
  const canUpdateStatus = canAccess('update-status');
  const canClosure = canAccess('closure');
  const canReopen = canAccess('reopen');
  const canManagerQueue = canAccess('manager-queue');
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentMsg, setCommentMsg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ subject: '', description: '', priority: '', category: '', service: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [chatUnread, setChatUnread] = useState(0);
  const [workSummary, setWorkSummary] = useState(null);
  const [workLoading, setWorkLoading] = useState(false);
  const [workError, setWorkError] = useState(null);

  const listFrom = resolveListFrom(location);
  const backLabel = backLabelForPath(listFrom);

  const loadTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ticketRes, commentsRes] = await Promise.all([
        axios.get(`${API}/${ticketNo}`, authHeaders()),
        fetchTicketComments(ticketNo).catch(() => []),
      ]);
      setTicket(ticketRes.data);
      setComments(Array.isArray(commentsRes) ? commentsRes : commentsRes.comments || []);
    } catch (err) {
      setError(formatApiError(err, 'Ticket not found'));
    } finally {
      setLoading(false);
    }
  };

  const loadWorkSummary = async () => {
    setWorkLoading(true);
    setWorkError(null);
    try {
      const res = await axios.get(`${API}/${ticketNo}/work-summary`, authHeaders());
      setWorkSummary(res.data);
    } catch (err) {
      setWorkSummary(null);
      setWorkError(formatApiError(err, 'Failed to load work time'));
    } finally {
      setWorkLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
    setDetailTab('overview');
    setEditing(false);
    setWorkSummary(null);
    setChatUnread(0);
  }, [ticketNo]);

  useEffect(() => {
    if (detailTab === 'work' && ticketNo) {
      loadWorkSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, ticketNo]);

  // Unread badge for the Chat tab. While the Chat tab is open, TicketClientChat
  // already polls and reports the count via onUnreadChange, so this one stops.
  const currentTicketRef = useRef(ticketNo);
  currentTicketRef.current = ticketNo;
  const pullChatUnread = useCallback(async () => {
    if (!ticketNo) return;
    try {
      const res = await axios.get(`${API}/${ticketNo}/chat`, {
        ...authHeaders(),
        params: { mark_read: false },
      });
      if (currentTicketRef.current === ticketNo) setChatUnread(res.data?.unread?.staff_unread || 0);
    } catch {
      if (currentTicketRef.current === ticketNo) setChatUnread(0);
    }
  }, [ticketNo]);

  useEffect(() => {
    pullChatUnread();
  }, [pullChatUnread]);

  useVisibleInterval(pullChatUnread, 20000, Boolean(ticketNo) && detailTab !== 'chat');

  const goBack = () => {
    navigate(listFrom);
  };

  const goAction = (actionPath) => {
    navigateTicketAction(navigate, location, ticket.ticket_no, actionPath);
  };

  const ticketUnassigned = !(ticket?.assigned_to?.length);
  const canDeleteTicket = isPlatformAdmin && (
    ticket?.status === 'cancelled'
    || (ticket?.status === 'open' && ticketUnassigned)
  );

  const handleDeleteTicket = async () => {
    if (!ticket || !canDeleteTicket) return;
    const kind = ticket.status === 'cancelled' ? 'cancelled' : 'open unassigned';
    const ok = window.confirm(
      `Delete ${kind} ticket ${ticket.ticket_no} permanently?\n\n`
      + 'This cannot be undone. Related comments, assignments, and attachments will be removed.',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/${encodeURIComponent(ticket.ticket_no)}`, authHeaders());
      notifySuccess(`Deleted ${ticket.ticket_no}`);
      navigate(listFrom || '/dashboard/tickets');
    } catch (err) {
      notifyError(formatApiError(err, 'Failed to delete ticket'));
    } finally {
      setDeleting(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      // Do NOT set Content-Type manually — axios must add the multipart boundary.
      await axios.post(`${API}/${ticketNo}/attachments`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadMsg(`Uploaded: ${file.name}`);
      await loadTicket();
    } catch (err) {
      setUploadMsg(formatApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setCommentSubmitting(true);
    setCommentMsg(null);
    try {
      await axios.post(`${API}/${ticketNo}/comments`, { body: newComment.trim() }, authHeaders());
      setNewComment('');
      setCommentMsg('Comment added');
      const commentsRes = await fetchTicketComments(ticketNo);
      setComments(Array.isArray(commentsRes) ? commentsRes : commentsRes.comments || []);
    } catch (err) {
      setCommentMsg(formatApiError(err, 'Failed to add comment'));
    } finally {
      setCommentSubmitting(false);
    }
  };

  const startEdit = () => {
    const reporter = ticket.reporter || {};
    setEditForm({
      domain: ticket.domain || '',
      category: ticket.category || '',
      service: ticket.service || '',
      subject: ticket.subject || '',
      description: ticket.description || '',
      priority: ticket.priority || 'medium',
      reporter_name: reporter.name || ticket.created_by_name || '',
      reporter_phone: reporter.phone || '',
      reporter_email: reporter.email || '',
      reporter_department: reporter.department || '',
      contact_method: reporter.contact_method || 'phone',
      product_id: ticket.product_id || '',
      domain_fields: { ...(ticket.domain_fields || {}) },
    });
    setEditing(true);
    setEditMsg(null);
    setDetailTab('overview');
  };

  const handleEditFormChange = (updates) => {
    setEditForm((f) => ({ ...f, ...updates }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (editForm.domain === 'product' && !editForm.product_id) {
      setEditMsg('Please select a product');
      return;
    }
    setEditSaving(true);
    setEditMsg(null);
    try {
      const payload = {
        domain: editForm.domain,
        category: editForm.category,
        service: editForm.service,
        subject: editForm.subject,
        description: editForm.description,
        priority: editForm.priority,
        domain_fields: editForm.domain_fields || {},
        reporter: {
          name: editForm.reporter_name,
          email: editForm.reporter_email,
          phone: editForm.reporter_phone,
          department: editForm.reporter_department,
          contact_method: editForm.contact_method,
        },
      };
      if (editForm.domain === 'product') {
        payload.product_id = editForm.product_id;
      } else {
        payload.product_id = null;
      }
      await axios.patch(`${API}/${ticketNo}`, payload, authHeaders());
      setEditMsg('Ticket updated');
      setEditing(false);
      await loadTicket();
    } catch (err) {
      setEditMsg(formatApiError(err, 'Update failed'));
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <SkeletonBlock rows={10} />;
  if (error) {
    return (
      <div>
        <Alert variant="error">{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>
        <button type="button" onClick={goBack} className="tk-back-btn">
          <ArrowLeft size={14} weight="bold" /> {backLabel}
        </button>
      </div>
    );
  }

  const reporter = ticket.reporter || {};
  const domainFields = ticket.domain_fields || {};
  const productName = ticketProductLabel(ticket);
  const attachments = ticket.attachments || [];
  const timeline = ticket.timeline || ticket.events || [];
  const viewOnly = ticket.view_only === true || ticket.can_mutate === false;
  const canEditTicket = !viewOnly && isTicketEditable(ticket.status);
  const canAttachFiles = !viewOnly && canUploadTicketAttachment(ticket.status);
  const isCreator = ticket.created_by === user?.user_id;
  const showEditButton = !viewOnly && canEditTicket && (isStaff || isCreator);
  const showActions = !viewOnly && (
    canManagerQueue || canTransfer || canUpdateStatus || canClosure || canReopen || showEditButton || canDeleteTicket
  );
  const attachmentCount = attachments.length
    + (ticket.attachment_filename && !attachments.some((a) => a.filename === ticket.attachment_filename) ? 1 : 0);

  return (
    <div className="tk-detail-page">
      <div className="tk-detail-hero">
        <div className="tk-detail-hero-main">
          <div className="tk-detail-hero-top">
            <button type="button" onClick={goBack} className="tk-back-btn" aria-label={backLabel}>
              <ArrowLeft size={14} weight="bold" />
              {backLabel}
            </button>
          </div>
          <h2>{ticket.ticket_no}</h2>
          <p className="tk-subject">{ticket.subject}</p>
          <div className="tk-meta-row">
            <TicketDomainBadge domain={ticket.domain} />
            <TicketStatusBadge status={ticket.status} />
            {viewOnly && (
              <span className="tk-pill" style={{ background: '#FFF7ED', color: '#9A3412', borderColor: '#FED7AA' }}>
                View only
              </span>
            )}
            <span className="tk-pill" style={{ background: '#F1F3F4', color: '#3C4043', borderColor: '#DADCE0', textTransform: 'capitalize' }}>
              {ticket.priority || '—'} priority
            </span>
            {ticket.assignee_names?.length ? (
              <span style={{ fontSize: '12px', color: '#44474E', fontWeight: 500 }}>{ticketAssigneeLabel(ticket)}</span>
            ) : null}
          </div>
        </div>

        {showActions && (
          <div className="tk-detail-hero-actions">
            {canManagerQueue && (
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" onClick={() => goAction('/dashboard/tickets/manager-queue')}>
                Queue
              </button>
            )}
            {canTransfer && (
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" onClick={() => goAction('/dashboard/tickets/transfer')}>
                Transfer
              </button>
            )}
            {canUpdateStatus && (
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" onClick={() => goAction('/dashboard/tickets/update-status')}>
                Status
              </button>
            )}
            {canClosure && (
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" onClick={() => goAction('/dashboard/tickets/closure')}>
                Closure
              </button>
            )}
            {canReopen && (
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" onClick={() => goAction('/dashboard/tickets/reopen')}>
                Reopen
              </button>
            )}
            {!editing && showEditButton && (
              <button type="button" className="tk-btn tk-btn-primary tk-btn-sm" onClick={startEdit}>Edit</button>
            )}
            {!viewOnly && canDeleteTicket && (
              <button
                type="button"
                className="tk-btn tk-btn-sm"
                style={{ color: '#B91C1C', borderColor: '#FECACA' }}
                disabled={deleting}
                onClick={handleDeleteTicket}
                title="Permanently delete this ticket (admin only — cancelled or open & unassigned)"
              >
                <Trash size={14} /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        )}
      </div>

      {viewOnly && (
        <Alert variant="info">
          You are viewing a shared ticket link (read-only). Sign in with a product/team account that owns this ticket to edit, comment, or change status.
        </Alert>
      )}

      {!viewOnly && !canEditTicket && (
        <Alert variant="info">
          This ticket can no longer be edited or receive new attachments.
          {['closed', 'cancelled'].includes(ticket.status)
            ? ' Use Reopen if it needs to be worked on again.'
            : ' Changes are locked once the ticket leaves the open state.'}
        </Alert>
      )}

      {editing && (
        <Surface className="tk-surface-section">
          <SectionTitle>Edit ticket</SectionTitle>
          <p style={{ fontSize: '12px', color: '#64748B', margin: '0 0 12px' }}>
            Change domain, product, and all related fields — same form as Create Ticket.
          </p>
          <DynamicTicketForm
            form={editForm}
            onChange={handleEditFormChange}
            onSubmit={handleSaveEdit}
            submitting={editSaving}
            error={editMsg && (editMsg.includes('failed') || editMsg.includes('denied') || editMsg.includes('select') || editMsg.includes('required')) ? editMsg : null}
            mode="edit"
            showAttachments={false}
            onCancel={() => { setEditing(false); setEditMsg(null); }}
          />
          {editMsg && !editMsg.includes('failed') && !editMsg.includes('denied') && !editMsg.includes('select') && !editMsg.includes('required') && (
            <Alert variant="success" style={{ marginTop: '10px' }}>{editMsg}</Alert>
          )}
        </Surface>
      )}

      {!editing && (
        <>
          <div className="tk-detail-tabs" role="tablist" aria-label="Ticket sections">
            {DETAIL_TABS.map((tab) => {
              const on = detailTab === tab.id;
              const count = tab.id === 'attachments'
                ? attachmentCount
                : tab.id === 'activity'
                  ? comments.length
                  : tab.id === 'chat'
                    ? chatUnread
                    : null;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`tk-detail-tab${on ? ' is-active' : ''}`}
                  onClick={() => setDetailTab(tab.id)}
                >
                  {tab.label}
                  {count != null && count > 0 ? (
                    <span className={`tk-detail-tab-count${tab.id === 'chat' ? ' tk-detail-tab-count--alert' : ''}`}>
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="tk-detail-tab-panel">
            {detailTab === 'work' && (
              <Surface className="tk-surface-section" style={{ marginBottom: 0 }}>
                <SectionTitle>Work time &amp; status durations</SectionTitle>
                <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px' }}>
                  Who worked this ticket, how long each person was active, and how long the ticket
                  spent in each status (for example In Progress → Completed, Completed → Closed).
                </p>
                <TicketWorkTimePanel summary={workSummary} loading={workLoading} error={workError} />
              </Surface>
            )}

            {detailTab === 'overview' && (
              <Surface interactive className="tk-surface-section" style={{ marginBottom: 0 }}>
                <SectionTitle>Details</SectionTitle>
                <div className="tk-fields-grid">
                  <Field label="Domain" value={ticket.domain} />
                  <Field label="Category" value={ticketCategoryLabel(ticket)} />
                  <Field label="Client / Branch" value={ticketClientBranchLabel(ticket)} />
                  {productName && <Field label="Product" value={productName} />}
                  <Field label="Priority" value={ticket.priority} />
                  <Field label="Ticket Submitted By" value={ticket.created_by_name || reporter.name} />
                  <Field label="Reported By" value={domainFields.reported_by || ticket.reported_by} />
                  <Field label="Department" value={reporter.department} />
                  <Field label="Contact" value={domainFields.contact_no || reporter.phone || ticket.contact_no} />
                  <Field label="Email" value={domainFields.reporter_email || reporter.email || ticket.email} />
                  <Field label="Contact Method" value={reporter.contact_method || ticket.intimate_through} />
                  <Field label="Created" value={formatUtcToIst(ticket.created_at)} />
                  {ticket.due_date && <Field label="SLA Due" value={formatUtcToIst(ticket.due_date)} />}
                  {ticket.assignee_names && (
                    <Field label="Assignees" value={ticket.assignee_names.join(', ')} />
                  )}
                </div>

                <div style={{ marginTop: '14px' }}>
                  <label className="tk-label">Description</label>
                  <div className="tk-field-readonly" style={{ minHeight: '80px', whiteSpace: 'pre-wrap', maxHeight: '220px', overflowY: 'auto' }}>
                    {ticket.description || ticket.problem_desc || '—'}
                  </div>
                </div>

                {ticket.update_reason && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="tk-label">Reason for Cause</label>
                    <div className="tk-field-readonly">{ticket.update_reason}</div>
                  </div>
                )}

                {ticket.reason_for_resolution && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="tk-label">Reason for Resolution</label>
                    <div className="tk-field-readonly">{ticket.reason_for_resolution}</div>
                  </div>
                )}

                {Object.keys(domainFields).length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <SectionTitle>Domain Fields</SectionTitle>
                    <div className="tk-table-wrap" style={{ boxShadow: 'none', maxHeight: '240px', overflow: 'auto' }}>
                      <table className="tk-table">
                        <tbody>
                          {Object.entries(domainFields).filter(([, v]) => v).map(([key, value]) => (
                            <tr key={key}>
                              <th style={{ width: '180px', textTransform: 'none', letterSpacing: 0 }}>{key.replace(/_/g, ' ')}</th>
                              <td>{String(value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Surface>
            )}

            {detailTab === 'attachments' && (
              <Surface className="tk-surface-section" style={{ marginBottom: 0 }}>
                <SectionTitle>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Paperclip size={14} /> Attachments
                  </span>
                </SectionTitle>
                {attachments.length === 0 && !ticket.attachment_filename ? (
                  <p style={{ fontSize: '12px', color: '#94A3B8', margin: '0 0 8px' }}>No attachments</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', margin: '0 0 12px', maxHeight: '360px', overflowY: 'auto' }}>
                    {attachments.map((a) => (
                      <AttachmentView key={a.filename || a.file_id} ticketNo={ticketNo} attachment={a} />
                    ))}
                    {ticket.attachment_filename && !attachments.some((a) => a.filename === ticket.attachment_filename) && (
                      <AttachmentView ticketNo={ticketNo} attachment={{ filename: ticket.attachment_filename }} />
                    )}
                  </div>
                )}
                {canAttachFiles ? (
                  <input type="file" onChange={handleUpload} disabled={uploading} style={{ fontSize: '12px' }} />
                ) : (
                  <p style={{ fontSize: '12px', color: '#94A3B8', margin: 0 }}>
                    Attachments cannot be added when the ticket is {ticket.status?.replace(/_/g, ' ') || 'closed'}.
                  </p>
                )}
                {uploadMsg && (
                  <p style={{ fontSize: '12px', marginTop: '8px', color: uploadMsg.startsWith('Uploaded') ? '#059669' : '#B91C1C' }}>
                    {uploadMsg}
                  </p>
                )}
              </Surface>
            )}

            {detailTab === 'chat' && (
              <TicketClientChat
                ticketNo={ticketNo}
                ticket={ticket}
                viewOnly={viewOnly}
                onUnreadChange={setChatUnread}
              />
            )}

            {detailTab === 'activity' && (
              <div className="tk-detail-activity">
                <Surface className="tk-surface-section">
                  <SectionTitle>Timeline</SectionTitle>
                  <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    <TicketTimeline timeline={timeline} />
                  </div>
                </Surface>

                <Surface className="tk-surface-section" style={{ marginBottom: 0 }}>
                  <SectionTitle>Comments</SectionTitle>
                  <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '12px' }}>
                    {comments.length === 0 ? (
                      <p style={{ color: '#94A3B8', fontSize: '12px', margin: 0 }}>No comments yet.</p>
                    ) : (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {comments.map((c, idx) => (
                          <div
                            key={c.comment_id || c.id || c.created_at}
                            className="tk-comment"
                            style={{ animationDelay: `${Math.min(idx, 6) * 40}ms` }}
                          >
                            <p>{c.body || c.comment || c.text}</p>
                            <p className="tk-comment-meta">
                              {c.author_name || c.created_by_name || 'User'} · {formatUtcToIst(c.created_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {viewOnly ? (
                    <p style={{ fontSize: '12px', color: '#94A3B8', margin: 0 }}>
                      Comments are read-only on a shared ticket link.
                    </p>
                  ) : (
                    <>
                      <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <textarea
                          className="tk-field"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          rows={2}
                          placeholder="Add a comment…"
                          style={{ flex: 1 }}
                        />
                        <button type="submit" disabled={commentSubmitting} className="tk-btn tk-btn-primary">
                          {commentSubmitting ? '…' : 'Post'}
                        </button>
                      </form>
                      {commentMsg && (
                        <p style={{ fontSize: '12px', marginTop: '8px', color: commentMsg === 'Comment added' ? '#059669' : '#B91C1C' }}>
                          {commentMsg}
                        </p>
                      )}
                    </>
                  )}
                </Surface>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TicketDetailPage;
